import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { isNil } from "lodash";

import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";

import { translationKeys } from "../../../../models/app-translation-keys";
import { useChatService } from "../../../../services/ServiceHooks";
import { ICustomKnowledgeBaseReadChatbot } from "../../../../stores";
import { useStore } from "../../../../stores/StoreContext";
import { KnowledgeBaseDropdownItem } from "./KnowledgeBaseDropdownItem/KnowledgeBaseDropdownItem";
import { KnowledgeBaseDropdownValue } from "./KnowledgeBaseDropdownValue/KnowledgeBaseDropdownValue";

import "./KnowledgeBaseSelection.scss";

const RawKnowledgeBaseSelection: FunctionComponent = () => {
  const { t } = useTranslation();

  const { chatStore, threadStore } = useStore();

  const chatService = useChatService();

  const [knowledgeBases, setKnowledgeBases] = useState<ICustomKnowledgeBaseReadChatbot[]>([]);

  const setInitialSelectedKnowledgeBases = (knowledgeBases: ICustomKnowledgeBaseReadChatbot[]) => {
    const kBFromStorage = chatService.getSelectedKnowledgeBaseFromLocalStorage();

    if (kBFromStorage?.id) {
      const knowledgeBaseFromStorage = kBFromStorage as ICustomKnowledgeBaseReadChatbot;
      const selectedKnowledgeBase = knowledgeBases.find(kb => kb.siteId === knowledgeBaseFromStorage.siteId);
      if (!isNil(selectedKnowledgeBase)) {
        // Set selected knowledgebase to the one saved in local storage
        chatService.setSelectedKnowledgeBase(selectedKnowledgeBase);
        return;
      }
    }

    if (!isNil(kBFromStorage) && !kBFromStorage.id && kBFromStorage.isDefault) {
      // Set the default knowledge base if it has been saved to local storage
      chatService.setSelectedKnowledgeBase(chatStore.defaultKnowledgeBase);
      return;
    }

    const newDefaultKnowledgeBase = knowledgeBases[0];
    if (newDefaultKnowledgeBase?.id) {
      // Set selected knowledgebase to the new default knowledge base from the API
      chatService.setSelectedKnowledgeBase(newDefaultKnowledgeBase);
    } else {
      // Set root default knowledgebase if no knowledge base is found in local storage and no default knowledge base is found in the API
      chatService.setSelectedKnowledgeBase(chatStore.defaultKnowledgeBase);
    }
  };

  useEffect(() => {
    const requestKnowledgeBases = async () => {
      chatStore.isKnowledgeBaseSelectionLoading = true;

      let response: ICustomKnowledgeBaseReadChatbot[] = await chatService.getKnowledgeBases();

      // Add siteId to each knowledge base for unique identification
      response = response.map(kb => ({
        ...kb,
        siteId: kb.id,
      }));

      if (response && Array.isArray(response)) {
        chatStore.knowledgeBases = [chatStore.defaultKnowledgeBase, ...response];
        setInitialSelectedKnowledgeBases(response);
      } else {
        chatStore.knowledgeBases = [];
      }

      chatStore.isKnowledgeBaseSelectionLoading = false;
    };

    if (!knowledgeBases?.length) {
      requestKnowledgeBases();
    }
  }, []);

  useEffect(() => {
    setKnowledgeBases(chatStore.knowledgeBases);
  }, [chatStore.knowledgeBases]);

  useEffect(() => {
    // If there is no active thread, we don't need to change the selected knowledge base
    if (!threadStore.activeThread) {
      return;
    }

    // If the active thread has no knowledge base ID, we reset the selected knowledge base to the default one
    const newSelectedKnowledgeBaseId = threadStore.activeThread?.knowledgeBaseId;
    if (!newSelectedKnowledgeBaseId && !!knowledgeBases.length && chatStore.selectedKnowledgeBase.id) {
      return;
    }

    const knowledgeBase = knowledgeBases.find(kb => kb.id === newSelectedKnowledgeBaseId);
    chatService.setSelectedKnowledgeBase(knowledgeBase ?? chatStore.defaultKnowledgeBase);
  }, [threadStore.activeThread, knowledgeBases.length]);

  const changeKnowledgeBases = async (newKnowledgeBase: ICustomKnowledgeBaseReadChatbot) => {
    threadStore.resetActiveThread();

    chatService.setSelectedKnowledgeBase(newKnowledgeBase);
  };

  const translatedKnowledgeBaseName = (knowledgeBase: ICustomKnowledgeBaseReadChatbot) => {
    if (!knowledgeBase) {
      return "";
    }

    if (knowledgeBase.isDefault && !knowledgeBase.id) {
      return t(knowledgeBase.name ?? "");
    } else {
      return knowledgeBase.name;
    }
  };

  const dropdownItemTemplate = (item: ICustomKnowledgeBaseReadChatbot) => {
    return <KnowledgeBaseDropdownItem item={item} translatedKnowledgeBaseName={translatedKnowledgeBaseName} />;
  };

  const dropdownValueTemplate = (item: ICustomKnowledgeBaseReadChatbot) =>
    !chatStore.isKnowledgeBaseSelectionLoading && !!item ? (
      <KnowledgeBaseDropdownValue item={item} translatedKnowledgeBaseName={translatedKnowledgeBaseName} />
    ) : (
      <span>{t(translationKeys.feature.chatbot.actions.loadingKnowledgeBases)}</span>
    );

  return (
    <div className="knowledge-base-selection-container">
      {chatStore.isKnowledgeBaseSelectionLoading ? (
        <ProgressSpinner className="small-inline-spinner" />
      ) : (
        <Dropdown
          value={chatStore.selectedKnowledgeBase}
          onChange={event => changeKnowledgeBases(event.value)}
          options={knowledgeBases}
          className="knowledge-base-selection-dropdown"
          panelClassName="dropdown-tooltip"
          filter={knowledgeBases.length > 1}
          filterBy="name"
          placeholder="Default"
          filterInputAutoFocus={true}
          showFilterClear={true}
          filterPlaceholder={t(translationKeys.feature.chatbot.knowledgebases.filterPlaceholder)}
          itemTemplate={dropdownItemTemplate}
          valueTemplate={dropdownValueTemplate}
          dataKey="siteId"
        />
      )}
    </div>
  );
};

export const KnowledgeBaseSelection = observer(RawKnowledgeBaseSelection);
